import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Plus, ArrowLeft, Pencil, Trash2, Send, Ban, X } from "lucide-react";

import axios from "../../../services/axiosInstance";
import { fetchMasterWarehouses } from "../raking/warehouseSlice";
import {
    PageHeader,
    Button,
    SearchInput,
    Segmented,
    Field,
    inputClass,
    Modal,
    Pager,
    errorMessage
} from "../../components/MasterUi";

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");
const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtDate = (s) => {
    if (!s) return "-";
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (!m) return String(s);
    const d = `${Number(m[3])} ${BULAN[Number(m[2]) - 1]} ${m[1]}`;
    return m[4] ? `${d}, ${m[4]}:${m[5]}` : d;
};

const STATUS_STYLE = {
    Draft: "bg-amber-50 text-amber-800",
    Posted: "bg-emerald-50 text-emerald-700",
    Cancelled: "bg-surface text-muted"
};
const STATUS_LABEL = { Draft: "Draft", Posted: "Diposting", Cancelled: "Dibatalkan" };

function StatusPill({ status }) {
    return (
        <span className={`inline-flex items-center text-xs font-semibold rounded-full px-2.5 py-1 ${STATUS_STYLE[status] || "bg-surface text-muted"}`}>
            {STATUS_LABEL[status] || status}
        </span>
    );
}

/* ================= combobox produk ================= */

function ProductPicker({ products, value, onChange, error }) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    const selected = products.find((p) => p.sku === value);

    useEffect(() => {
        const onDown = (e) => !boxRef.current?.contains(e.target) && setOpen(false);
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q
            ? products.filter((p) => p.sku?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q))
            : products;
        return list.slice(0, 40);
    }, [products, query]);

    return (
        <div className="relative" ref={boxRef}>
            <input
                type="text"
                value={open ? query : selected ? `${selected.sku} - ${selected.name}` : value || ""}
                onFocus={() => {
                    setQuery("");
                    setOpen(true);
                }}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                }}
                placeholder="Ketik SKU atau nama produk"
                className={inputClass(error)}
                role="combobox"
                aria-expanded={open}
            />
            {open && (
                <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-line rounded-md shadow-lg py-1">
                    {results.length === 0 ? (
                        <li className="px-3 py-2 text-sm text-muted">Produk tidak ditemukan.</li>
                    ) : (
                        results.map((p) => (
                            <li key={p.id}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onChange(p.sku);
                                        setOpen(false);
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-surface flex gap-3 ${
                                        p.sku === value ? "bg-ink-100" : ""
                                    }`}
                                >
                                    <span className="tabular-nums text-muted w-24 shrink-0">{p.sku}</span>
                                    <span className="text-body truncate">{p.name}</span>
                                </button>
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
}

/* ================= daftar dokumen ================= */

function BeginningBalanceList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const warehouses = useSelector((s) => s.masterWarehouses?.data) || [];

    const [warehouseId, setWarehouseId] = useState("");
    const [status, setStatus] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [res, setRes] = useState({ data: [], pagination: {}, counts: {} });
    const [loading, setLoading] = useState(false);

    const [openCreate, setOpenCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ warehouse_id: "", remarks: "" });
    const [createErr, setCreateErr] = useState({});
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (!warehouses.length) dispatch(fetchMasterWarehouses());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => setPage(1), [warehouseId, status, search]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        axios
            .get("/beginning-balance", {
                params: {
                    warehouse_id: warehouseId || undefined,
                    status: status || undefined,
                    search: search || undefined,
                    page,
                    per_page: 20
                }
            })
            .then((r) => alive && setRes({ data: r.data?.data || [], pagination: r.data?.pagination || {}, counts: r.data?.counts || {} }))
            .catch((err) => alive && toast.error(errorMessage(err.response?.data, "Daftar saldo awal tidak bisa diambil.")))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [warehouseId, status, search, page]);

    const counts = res.counts || {};
    const totalAll = Object.values(counts).reduce((a, n) => a + Number(n), 0);

    const submitCreate = async (e) => {
        e.preventDefault();
        if (!createForm.warehouse_id) {
            setCreateErr({ warehouse_id: "Pilih warehouse." });
            return;
        }
        try {
            setCreating(true);
            const r = await axios.post("/beginning-balance", {
                warehouse_id: Number(createForm.warehouse_id),
                remarks: createForm.remarks || null
            });
            toast.success(`Dokumen ${r.data?.data?.bb_number} dibuat.`);
            navigate(`/admin/beginning-balance/${r.data?.data?.id}`);
        } catch (err) {
            toast.error(errorMessage(err.response?.data, "Dokumen gagal dibuat."));
        } finally {
            setCreating(false);
        }
    };

    const thClass = "px-4 py-2.5 text-left text-xs font-medium text-muted bg-surface border-b border-line whitespace-nowrap";
    const tdClass = "px-4 py-3 border-b border-line text-sm";

    return (
        <div className="max-w-[1600px] mx-auto space-y-5">
            <PageHeader title="Beginning balance" subtitle="Dokumen saldo awal stok di rak. Stok baru masuk ke rak setelah dokumen diposting.">
                <Button
                    variant="primary"
                    onClick={() => {
                        setCreateForm({ warehouse_id: warehouseId || "", remarks: "" });
                        setCreateErr({});
                        setOpenCreate(true);
                    }}
                >
                    <Plus size={16} /> Buat dokumen
                </Button>
            </PageHeader>

            <section className="bg-white rounded-lg border border-line">
                <div className="flex flex-col xl:flex-row xl:items-center gap-3 px-5 py-4 border-b border-line">
                    <select
                        value={warehouseId}
                        onChange={(e) => setWarehouseId(e.target.value)}
                        aria-label="Warehouse"
                        className="h-10 xl:w-56 bg-white border border-line rounded-md px-3 text-sm text-body focus:border-ink focus:ring-0"
                    >
                        <option value="">Semua warehouse</option>
                        {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                    </select>
                    <SearchInput value={searchInput} onChange={setSearchInput} placeholder="Cari nomor dokumen atau keterangan" className="xl:w-80" />
                    <div className="xl:ml-auto">
                        <Segmented
                            label="Status"
                            value={status}
                            onChange={setStatus}
                            options={[
                                { value: "", label: `Semua ${fmt(totalAll)}` },
                                { value: "Draft", label: `Draft ${fmt(counts.Draft)}` },
                                { value: "Posted", label: `Diposting ${fmt(counts.Posted)}` },
                                { value: "Cancelled", label: `Dibatalkan ${fmt(counts.Cancelled)}` }
                            ]}
                        />
                    </div>
                </div>

                <div className={`overflow-x-auto transition-opacity ${loading ? "opacity-50" : ""}`}>
                    <table className="w-full border-collapse min-w-[860px]">
                        <thead>
                            <tr>
                                <th className={thClass}>No. dokumen</th>
                                <th className={thClass}>Warehouse</th>
                                <th className={thClass}>Status</th>
                                <th className={`${thClass} text-right`}>Baris</th>
                                <th className={`${thClass} text-right`}>Total qty</th>
                                <th className={thClass}>Dibuat</th>
                                <th className={thClass}>Diposting</th>
                            </tr>
                        </thead>
                        <tbody>
                            {res.data.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-14 text-center text-sm text-muted">
                                        {loading
                                            ? "Memuat dokumen…"
                                            : search || status || warehouseId
                                                ? "Tidak ada dokumen yang cocok dengan filter ini."
                                                : "Belum ada dokumen saldo awal. Buat dokumen pertama untuk mulai mencatat stok awal di rak."}
                                    </td>
                                </tr>
                            ) : (
                                res.data.map((d) => (
                                    <tr
                                        key={d.id}
                                        onClick={() => navigate(`/admin/beginning-balance/${d.id}`)}
                                        className="cursor-pointer hover:bg-surface/70"
                                    >
                                        <td className={tdClass}>
                                            <div className="font-semibold text-ink tabular-nums">{d.bb_number}</div>
                                            {d.remarks && <div className="text-xs text-muted truncate max-w-[280px]">{d.remarks}</div>}
                                        </td>
                                        <td className={`${tdClass} text-muted`}>{d.warehouse_name || "-"}</td>
                                        <td className={tdClass}><StatusPill status={d.status} /></td>
                                        <td className={`${tdClass} text-right tabular-nums`}>{fmt(d.total_rows)}</td>
                                        <td className={`${tdClass} text-right tabular-nums font-semibold`}>{fmt(d.sum_qty)}</td>
                                        <td className={tdClass}>
                                            <div className="tabular-nums">{fmtDate(d.created_at)}</div>
                                            <div className="text-xs text-muted">{d.created_by_name || "-"}</div>
                                        </td>
                                        <td className={tdClass}>
                                            {d.posted_at ? (
                                                <>
                                                    <div className="tabular-nums">{fmtDate(d.posted_at)}</div>
                                                    <div className="text-xs text-muted">{d.posted_by_name || "-"}</div>
                                                </>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {res.pagination?.total > 0 && (
                    <div className="px-5 py-3">
                        <Pager pagination={res.pagination} unit="dokumen" onChange={setPage} />
                    </div>
                )}
            </section>

            <Modal
                open={openCreate}
                onClose={() => !creating && setOpenCreate(false)}
                title="Buat dokumen saldo awal"
                subtitle="Dokumen dibuat sebagai Draft. Baris stok diisi setelahnya."
                size="sm"
                footer={
                    <>
                        <Button onClick={() => setOpenCreate(false)} disabled={creating}>Batal</Button>
                        <Button variant="primary" type="submit" form="form-bb-create" disabled={creating}>
                            {creating ? "Membuat…" : "Buat dokumen"}
                        </Button>
                    </>
                }
            >
                <form id="form-bb-create" onSubmit={submitCreate} className="space-y-4">
                    <Field label="Warehouse" error={createErr.warehouse_id} hint="Semua rak di dokumen ini harus milik warehouse yang dipilih.">
                        <select
                            value={createForm.warehouse_id}
                            onChange={(e) => setCreateForm({ ...createForm, warehouse_id: e.target.value })}
                            className={inputClass(createErr.warehouse_id)}
                            autoFocus
                        >
                            <option value="">Pilih warehouse</option>
                            {warehouses.map((w) => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Keterangan" hint="Opsional, mis. Saldo awal go-live Oktober 2026.">
                        <textarea
                            rows={3}
                            value={createForm.remarks}
                            onChange={(e) => setCreateForm({ ...createForm, remarks: e.target.value })}
                            className="w-full bg-white border border-line rounded-md px-3 py-2 text-sm text-body focus:border-ink focus:ring-0"
                        />
                    </Field>
                </form>
            </Modal>
        </div>
    );
}

/* ================= detail dokumen ================= */

const EMPTY_ROW = { rack_code: "", level_code: "", pallet_code: "", box_code: "", sku: "", batch_no: "", due_date: "", qty: "" };

function BeginningBalanceDetail({ id }) {
    const navigate = useNavigate();

    const [doc, setDoc] = useState(null);
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [racks, setRacks] = useState([]);
    const [products, setProducts] = useState([]);

    const [row, setRow] = useState(EMPTY_ROW);
    const [editId, setEditId] = useState(null);
    const [rowErr, setRowErr] = useState({});
    const [saving, setSaving] = useState(false);

    const [rowErrors, setRowErrors] = useState({}); // error posting per detail id
    const [confirm, setConfirm] = useState(null); // 'post' | 'cancel' | {delete: detail}
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("");

    const isDraft = doc?.status === "Draft";

    const applyResponse = (r) => {
        setDoc(r.data?.data || null);
        setDetails(r.data?.details || []);
    };

    const load = () => {
        setLoading(true);
        return axios
            .get(`/beginning-balance/${id}`)
            .then(applyResponse)
            .catch((err) => {
                if (err.response?.status === 404) setNotFound(true);
                else toast.error(errorMessage(err.response?.data, "Dokumen tidak bisa diambil."));
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        axios
            .get("/products", { params: { page: 1, per_page: 3000 } })
            .then((r) => setProducts(r.data?.data || []))
            .catch(() => setProducts([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!doc?.warehouse_id) return;
        axios
            .get("/beginning-balance/lookup/racks", { params: { warehouse_id: doc.warehouse_id } })
            .then((r) => setRacks(r.data?.data || []))
            .catch(() => setRacks([]));
    }, [doc?.warehouse_id]);

    const rack = racks.find((r) => r.code === row.rack_code);
    const slotOptions = useMemo(() => {
        if (!rack) return [];
        const out = [];
        for (let r = 1; r <= Number(rack.total_rows || 0); r++) {
            for (let c = 1; c <= Number(rack.total_columns || 0); c++) out.push(`${r}.${c}`);
        }
        return out;
    }, [rack]);

    const productName = (sku) => products.find((p) => p.sku === sku)?.name;

    const resetRow = () => {
        setRow((prev) => ({ ...EMPTY_ROW, rack_code: prev.rack_code })); // rak dipertahankan untuk input berikutnya
        setEditId(null);
        setRowErr({});
    };

    const submitRow = async (e) => {
        e.preventDefault();
        const payload = { ...row, qty: row.qty === "" ? "" : Number(row.qty) };
        try {
            setSaving(true);
            const r = editId
                ? await axios.put(`/beginning-balance/${id}/details/${editId}`, payload)
                : await axios.post(`/beginning-balance/${id}/details`, payload);
            applyResponse(r);
            toast.success(editId ? "Baris diperbarui." : "Baris ditambahkan.");
            setRowErrors((prev) => {
                const n = { ...prev };
                if (editId) delete n[editId];
                return n;
            });
            resetRow();
        } catch (err) {
            const errs = err.response?.data?.errors;
            if (errs) setRowErr(errs[0] || Object.values(errs)[0] || {});
            toast.error(errorMessage(err.response?.data, "Baris gagal disimpan."));
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (d) => {
        setEditId(d.id);
        setRow({
            rack_code: d.rack_code || "",
            level_code: d.level_code || "",
            pallet_code: d.pallet_code || "",
            box_code: d.box_code || "",
            sku: d.sku || "",
            batch_no: d.batch_no || "",
            due_date: d.due_date ? String(d.due_date).slice(0, 10) : "",
            qty: d.qty ?? ""
        });
        setRowErr(rowErrors[d.id] || {});
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const doDelete = async (d) => {
        try {
            setBusy(true);
            const r = await axios.delete(`/beginning-balance/${id}/details/${d.id}`);
            applyResponse(r);
            if (editId === d.id) resetRow();
            toast.success("Baris dihapus.");
        } catch (err) {
            toast.error(errorMessage(err.response?.data, "Baris gagal dihapus."));
        } finally {
            setBusy(false);
            setConfirm(null);
        }
    };

    const doPost = async () => {
        try {
            setBusy(true);
            const r = await axios.post(`/beginning-balance/${id}/post`);
            applyResponse(r);
            setRowErrors({});
            toast.success(`Dokumen ${doc.bb_number} diposting. Stok sudah masuk ke rak.`);
        } catch (err) {
            const errs = err.response?.data?.errors;
            if (errs) setRowErrors(errs);
            toast.error(errorMessage(err.response?.data, "Posting gagal."));
        } finally {
            setBusy(false);
            setConfirm(null);
        }
    };

    const doCancel = async () => {
        try {
            setBusy(true);
            const r = await axios.post(`/beginning-balance/${id}/cancel`);
            applyResponse(r);
            toast.success(`Dokumen ${doc.bb_number} dibatalkan.`);
        } catch (err) {
            toast.error(errorMessage(err.response?.data, "Dokumen gagal dibatalkan."));
        } finally {
            setBusy(false);
            setConfirm(null);
        }
    };

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return details;
        return details.filter((d) =>
            [d.rack_code, d.racks_name, d.level_code, d.pallet_code, d.sku, d.product_name, d.batch_no]
                .some((v) => String(v ?? "").toLowerCase().includes(q))
        );
    }, [details, filter]);

    const totalQty = details.reduce((a, d) => a + Number(d.qty || 0), 0);
    const rackCount = new Set(details.map((d) => d.rack_code)).size;
    const errorCount = Object.keys(rowErrors).length;

    if (notFound) {
        return (
            <div className="max-w-[1600px] mx-auto">
                <div className="bg-white rounded-lg border border-line px-6 py-14 text-center">
                    <div className="font-semibold text-body">Dokumen tidak ditemukan</div>
                    <p className="text-sm text-muted mt-1">Dokumen saldo awal ini mungkin sudah dihapus atau nomornya salah.</p>
                    <Button className="mt-4" onClick={() => navigate("/admin/beginning-balance")}>
                        <ArrowLeft size={16} /> Kembali ke daftar
                    </Button>
                </div>
            </div>
        );
    }

    const thClass = "px-3 py-2.5 text-left text-xs font-medium text-muted bg-surface border-b border-line whitespace-nowrap";
    const tdClass = "px-3 py-2.5 border-b border-line text-sm align-top";

    return (
        <div className="max-w-[1600px] mx-auto space-y-5">

            {/* ===== judul ===== */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <button
                        type="button"
                        onClick={() => navigate("/admin/beginning-balance")}
                        className="inline-flex items-center gap-1 text-sm text-muted hover:text-body mb-2"
                    >
                        <ArrowLeft size={15} /> Daftar dokumen
                    </button>
                    <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-2xl font-semibold text-body tracking-tight tabular-nums">
                            {doc?.bb_number || (loading ? "Memuat…" : "-")}
                        </h2>
                        {doc && <StatusPill status={doc.status} />}
                    </div>
                    <p className="text-sm text-muted mt-1">
                        {doc?.warehouse_name || "-"}
                        {doc?.remarks ? `, ${doc.remarks}` : ""}
                    </p>
                    <p className="text-xs text-muted mt-1">
                        Dibuat {fmtDate(doc?.created_at)} oleh {doc?.created_by_name || "-"}
                        {doc?.posted_at && <> · Diposting {fmtDate(doc.posted_at)} oleh {doc.posted_by_name || "-"}</>}
                    </p>
                </div>

                {isDraft && (
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={() => setConfirm("cancel")} disabled={busy}>
                            <Ban size={16} /> Batalkan dokumen
                        </Button>
                        <Button variant="primary" onClick={() => setConfirm("post")} disabled={busy || details.length === 0}>
                            <Send size={16} /> Posting ke rak
                        </Button>
                    </div>
                )}
            </div>

            {/* ===== ringkasan ===== */}
            <div className="grid grid-cols-3 gap-3">
                {[
                    ["Baris stok", fmt(details.length)],
                    ["Rak", fmt(rackCount)],
                    ["Total qty", fmt(totalQty)]
                ].map(([label, value]) => (
                    <div key={label} className="bg-white rounded-lg border border-line px-4 py-3">
                        <div className="text-sm text-muted">{label}</div>
                        <div className="text-2xl font-semibold text-body tabular-nums mt-1">{value}</div>
                    </div>
                ))}
            </div>

            {doc?.status === "Posted" && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                    Dokumen sudah diposting. Stok di bawah sudah masuk ke rak dan tidak bisa diubah dari sini.
                </div>
            )}
            {doc?.status === "Cancelled" && (
                <div className="rounded-lg border border-line bg-surface px-4 py-3 text-sm text-muted">
                    Dokumen dibatalkan. Stok di dokumen ini tidak pernah masuk ke rak.
                </div>
            )}
            {errorCount > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {errorCount} baris belum valid, jadi posting dibatalkan. Baris yang bermasalah ditandai merah di tabel. Klik Ubah untuk memperbaikinya.
                </div>
            )}

            {/* ===== form baris ===== */}
            {isDraft && (
                <section className="bg-white rounded-lg border border-line">
                    <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-line">
                        <div>
                            <h3 className="text-base font-semibold text-body">{editId ? "Ubah baris" : "Tambah baris stok"}</h3>
                            <p className="text-xs text-muted mt-0.5">Satu baris = satu item dan lot di satu slot. Rak tetap terpilih untuk baris berikutnya.</p>
                        </div>
                        {editId && (
                            <Button variant="ghost" onClick={resetRow}>
                                <X size={16} /> Batal ubah
                            </Button>
                        )}
                    </div>

                    <form onSubmit={submitRow} className="px-5 py-4 grid grid-cols-1 md:grid-cols-6 gap-4">
                        <Field label="Rak" error={rowErr.rack_code} className="md:col-span-1">
                            <select
                                value={row.rack_code}
                                onChange={(e) => setRow({ ...row, rack_code: e.target.value, level_code: "" })}
                                className={inputClass(rowErr.rack_code)}
                            >
                                <option value="">Pilih rak</option>
                                {racks.map((r) => (
                                    <option key={r.id} value={r.code}>{r.racks_name || r.code} ({r.code})</option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Slot" error={rowErr.level_code} className="md:col-span-1">
                            <select
                                value={row.level_code}
                                onChange={(e) => setRow({ ...row, level_code: e.target.value })}
                                disabled={!rack}
                                className={inputClass(rowErr.level_code)}
                            >
                                <option value="">{rack ? "Pilih slot" : "Pilih rak dulu"}</option>
                                {slotOptions.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Produk" error={rowErr.sku} className="md:col-span-4">
                            <ProductPicker products={products} value={row.sku} onChange={(sku) => setRow({ ...row, sku })} error={rowErr.sku} />
                        </Field>

                        <Field label="Lot" error={rowErr.batch_no} className="md:col-span-1">
                            <input
                                type="text"
                                value={row.batch_no}
                                onChange={(e) => setRow({ ...row, batch_no: e.target.value.toUpperCase() })}
                                className={inputClass(rowErr.batch_no)}
                            />
                        </Field>

                        <Field label="ED (kedaluwarsa)" error={rowErr.due_date} hint="Opsional" className="md:col-span-1">
                            <input
                                type="date"
                                value={row.due_date}
                                onChange={(e) => setRow({ ...row, due_date: e.target.value })}
                                className={inputClass(rowErr.due_date)}
                            />
                        </Field>

                        <Field label="Qty" error={rowErr.qty} className="md:col-span-1">
                            <input
                                type="number"
                                min="1"
                                inputMode="numeric"
                                value={row.qty}
                                onChange={(e) => setRow({ ...row, qty: e.target.value })}
                                className={inputClass(rowErr.qty)}
                            />
                        </Field>

                        <Field label="Kode palet" error={rowErr.pallet_code} hint="Opsional, mis. PA0153" className="md:col-span-1">
                            <input
                                type="text"
                                value={row.pallet_code}
                                onChange={(e) => setRow({ ...row, pallet_code: e.target.value.toUpperCase() })}
                                className={inputClass(rowErr.pallet_code)}
                            />
                        </Field>

                        <Field label="Kode box" hint="Opsional" className="md:col-span-1">
                            <input
                                type="text"
                                value={row.box_code}
                                onChange={(e) => setRow({ ...row, box_code: e.target.value })}
                                className={inputClass(false)}
                            />
                        </Field>

                        <div className="md:col-span-1 flex items-end">
                            <Button variant="primary" type="submit" disabled={saving} className="w-full">
                                {saving ? "Menyimpan…" : editId ? "Simpan perubahan" : <><Plus size={16} /> Tambah baris</>}
                            </Button>
                        </div>
                    </form>
                </section>
            )}

            {/* ===== tabel baris ===== */}
            <section className="bg-white rounded-lg border border-line">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 border-b border-line">
                    <div>
                        <h3 className="text-base font-semibold text-body">Baris stok</h3>
                        <p className="text-xs text-muted mt-0.5">{fmt(filtered.length)} dari {fmt(details.length)} baris</p>
                    </div>
                    <SearchInput value={filter} onChange={setFilter} placeholder="Cari rak, slot, palet, SKU, atau lot" className="md:w-80" />
                </div>

                <div className={`overflow-x-auto ${loading ? "opacity-50" : ""}`}>
                    <table className="w-full border-collapse min-w-[1000px]">
                        <thead>
                            <tr>
                                <th className={thClass}>Rak</th>
                                <th className={thClass}>Slot</th>
                                <th className={thClass}>Produk</th>
                                <th className={thClass}>Lot</th>
                                <th className={thClass}>ED</th>
                                <th className={thClass}>Palet</th>
                                <th className={`${thClass} text-right`}>Qty</th>
                                <th className={thClass}>Status</th>
                                {isDraft && <th className={`${thClass} text-right`} />}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={isDraft ? 9 : 8} className="px-4 py-12 text-center text-sm text-muted">
                                        {loading
                                            ? "Memuat baris…"
                                            : filter
                                                ? `Tidak ada baris yang cocok dengan "${filter}".`
                                                : isDraft
                                                    ? "Belum ada baris. Isi form di atas lalu klik Tambah baris."
                                                    : "Dokumen ini tidak punya baris stok."}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((d) => {
                                    const errs = rowErrors[d.id];
                                    const editing = editId === d.id;
                                    return (
                                        <tr
                                            key={d.id}
                                            className={`${errs ? "bg-red-50/60" : editing ? "bg-ink-100/60" : "hover:bg-surface/60"}`}
                                        >
                                            <td className={tdClass}>
                                                <div className="font-semibold">{d.racks_name || d.rack_code}</div>
                                                <div className="text-xs text-muted">{d.rack_code}</div>
                                            </td>
                                            <td className={`${tdClass} tabular-nums`}>{d.level_code}</td>
                                            <td className={tdClass}>
                                                <div className="tabular-nums">{d.sku}</div>
                                                <div className="text-xs text-muted max-w-[280px] truncate" title={d.product_name || productName(d.sku)}>
                                                    {d.product_name || productName(d.sku) || "-"}
                                                </div>
                                                {errs && (
                                                    <ul className="mt-1 text-xs text-red-700 list-disc pl-4">
                                                        {Object.values(errs).map((m, i) => <li key={i}>{m}</li>)}
                                                    </ul>
                                                )}
                                            </td>
                                            <td className={`${tdClass} tabular-nums`}>{d.batch_no}</td>
                                            <td className={`${tdClass} tabular-nums whitespace-nowrap`}>{fmtDate(d.due_date)}</td>
                                            <td className={tdClass}>
                                                {d.pallet_code ? (
                                                    <span className="inline-block px-1.5 py-0.5 rounded bg-ink-100 text-ink text-xs font-semibold">{d.pallet_code}</span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className={`${tdClass} text-right tabular-nums font-semibold`}>{fmt(d.qty)}</td>
                                            <td className={tdClass}><StatusPill status={d.status} /></td>
                                            {isDraft && (
                                                <td className={`${tdClass} text-right whitespace-nowrap`}>
                                                    <button
                                                        type="button"
                                                        onClick={() => startEdit(d)}
                                                        className="inline-flex items-center gap-1 h-8 px-2.5 text-sm text-ink rounded-md hover:bg-ink-100"
                                                    >
                                                        <Pencil size={14} /> Ubah
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirm({ delete: d })}
                                                        className="inline-flex items-center gap-1 h-8 px-2.5 text-sm text-red-600 rounded-md hover:bg-red-50"
                                                    >
                                                        <Trash2 size={14} /> Hapus
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* ===== konfirmasi ===== */}
            <Modal
                open={confirm === "post"}
                onClose={() => !busy && setConfirm(null)}
                title={`Posting ${doc?.bb_number || ""}?`}
                subtitle="Stok akan langsung masuk ke rak dan dokumen tidak bisa diubah lagi."
                size="sm"
                footer={
                    <>
                        <Button onClick={() => setConfirm(null)} disabled={busy}>Batal</Button>
                        <Button variant="primary" onClick={doPost} disabled={busy}>
                            <Send size={16} /> {busy ? "Memposting…" : "Posting sekarang"}
                        </Button>
                    </>
                }
            >
                <dl className="grid grid-cols-3 gap-3 text-sm">
                    <div><dt className="text-xs text-muted">Baris</dt><dd className="text-lg font-semibold tabular-nums">{fmt(details.length)}</dd></div>
                    <div><dt className="text-xs text-muted">Rak</dt><dd className="text-lg font-semibold tabular-nums">{fmt(rackCount)}</dd></div>
                    <div><dt className="text-xs text-muted">Total qty</dt><dd className="text-lg font-semibold tabular-nums">{fmt(totalQty)}</dd></div>
                </dl>
                <p className="text-sm text-muted mt-4">
                    Semua baris diperiksa ulang sebelum posting. Kalau ada yang tidak valid, posting dibatalkan dan barisnya ditandai.
                </p>
            </Modal>

            <Modal
                open={confirm === "cancel"}
                onClose={() => !busy && setConfirm(null)}
                title={`Batalkan ${doc?.bb_number || ""}?`}
                subtitle="Dokumen yang dibatalkan tidak bisa diubah atau diposting lagi."
                size="sm"
                footer={
                    <>
                        <Button onClick={() => setConfirm(null)} disabled={busy}>Kembali</Button>
                        <Button onClick={doCancel} disabled={busy} className="!text-red-700 !border-red-200 hover:!bg-red-50">
                            <Ban size={16} /> {busy ? "Membatalkan…" : "Batalkan dokumen"}
                        </Button>
                    </>
                }
            >
                <p className="text-sm text-body">Stok di dokumen ini tidak akan masuk ke rak.</p>
            </Modal>

            <Modal
                open={Boolean(confirm?.delete)}
                onClose={() => !busy && setConfirm(null)}
                title="Hapus baris ini?"
                size="sm"
                footer={
                    <>
                        <Button onClick={() => setConfirm(null)} disabled={busy}>Batal</Button>
                        <Button onClick={() => doDelete(confirm.delete)} disabled={busy} className="!text-red-700 !border-red-200 hover:!bg-red-50">
                            <Trash2 size={16} /> {busy ? "Menghapus…" : "Hapus baris"}
                        </Button>
                    </>
                }
            >
                {confirm?.delete && (
                    <p className="text-sm text-body">
                        {confirm.delete.sku} lot {confirm.delete.batch_no}, qty {fmt(confirm.delete.qty)} di {confirm.delete.racks_name || confirm.delete.rack_code} slot {confirm.delete.level_code}.
                    </p>
                )}
            </Modal>
        </div>
    );
}

/* ================= router kecil ================= */

export default function BeginningBalancePage() {
    const { id } = useParams();
    return id ? <BeginningBalanceDetail id={id} /> : <BeginningBalanceList />;
}
